import { detectMimeTypeFromName } from "@prose-reader/shared"
import { from, map, of, switchMap, tap } from "rxjs"
import { DocumentRenderer } from "../../../spineItem/renderer/DocumentRenderer"
import { isHtmlTagElement, setAttributeIfChanged } from "../../../utils/dom"
import {
  upsertCSSToFrame,
  waitForFrameLoad,
  waitForFrameReady,
} from "../../../utils/frames"
import { waitForSwitch } from "../../../utils/rxjs"
import { loadAssets, unloadAssets } from "./assets"
import { attachFrameSrc, revokeFrameObjectUrl } from "./attachFrameSrc"
import { createFrameElement } from "./createFrameElement"
import prePaginatedStyle from "./prePaginated/pre-paginated.css?inline"
import { renderPrePaginated } from "./prePaginated/renderPrePaginated"
// import reflowableImageStyle from "./reflowable/reflowable-image.css?inline"
import { renderReflowable } from "./reflowable/renderReflowable"

export class HtmlRenderer extends DocumentRenderer {
  onCreateDocument() {
    const frameElement = createFrameElement(this.context.document)

    this.setDocumentContainer(frameElement)

    return of(frameElement)
  }

  onLoadDocument() {
    const frameElement = this.getFrameElement()

    if (!frameElement) throw new Error(`invalid frame`)

    return of(frameElement).pipe(
      attachFrameSrc({
        item: this.item,
        resourcesHandler: this.resourcesHandler,
        settings: this.settings,
      }),
      /**
       * Preload iframes only when the viewport is stable to avoid competing with
       * navigation/transition work on the main thread.
       */
      waitForSwitch(this.context.bridgeEvent.viewportFree$),
      tap((frameElement) => {
        /**
         * Attach hidden so parsing/layout/asset work can start off-screen.
         * We reveal only once load + assets + fonts are ready.
         */
        this.prepareFrameForAttach(frameElement)
        this.attach()
      }),
      waitForFrameLoad,
      tap((frameElement) => {
        if (this.isPrePaginated()) {
          upsertCSSToFrame(frameElement, `prose-reader-css`, prePaginatedStyle)
        } else {
          if (this.isImageType()) {
            // upsertCSSToFrame(
            //   frameElement,
            //   `prose-reader-css`,
            //   reflowableImageStyle,
            // )
          } else {
            // upsertCSSToFrame(frameElement, `prose-reader-css`, reflowableStyle)
          }
        }
      }),
      loadAssets({
        context: this.context,
        item: this.item,
        settings: this.settings,
      }),
      waitForFrameReady,
      tap((frameElement) => {
        this.revealFrameAfterReady(frameElement)
      }),
    )
  }

  onUnload() {
    const frameElement = this.getFrameElement()

    unloadAssets(frameElement)
    revokeFrameObjectUrl(frameElement)

    this.detach()
  }

  onLayout({
    minPageSpread,
    blankPagePosition,
    spreadPosition,
  }: {
    minPageSpread: number
    blankPagePosition: `before` | `after` | `none`
    spreadPosition: `none` | `left` | `right`
  }) {
    const { width: pageWidth, height: pageHeight } = this.viewport.pageSize
    const frameElement = this.getFrameElement()

    if (!frameElement) return of(undefined)

    /**
     * When we have scrollable content, we use "native" touch event from the frame instead of
     * our own gestures.
     * @todo move this into scroll navigator
     */
    const isTouchEnabled =
      this.settings.values.computedPageTurnMode === `scrollable`

    if (frameElement.contentDocument?.documentElement) {
      setAttributeIfChanged(
        frameElement.contentDocument.documentElement,
        `data-prose-reader-html-renderer-spread-position`,
        spreadPosition,
      )
      if (isTouchEnabled) {
        frameElement.contentDocument?.documentElement.classList.add(
          `prose-reader-html-renderer-touch-enabled`,
        )
      } else {
        frameElement.contentDocument?.documentElement.classList.remove(
          `prose-reader-html-renderer-touch-enabled`,
        )
      }
    }

    if (this.isPrePaginated()) {
      const dims = renderPrePaginated({
        blankPagePosition,
        frameElement,
        isRTL: this.context.isRTL(),
        minPageSpread,
        pageHeight,
        pageWidth,
        spreadPosition,
      })

      return of(dims)
    }

    const isUsingVerticalWriting = !!this.writingMode?.startsWith(`vertical`)
    const isImageType = this.isImageType()

    const dims = renderReflowable({
      pageHeight,
      pageWidth,
      frameElement,
      manifest: this.context.manifest,
      renditionFlow:
        this.item.renditionFlow ?? this.context.manifest.renditionFlow,
      blankPagePosition,
      isUsingVerticalWriting,
      isRTL: this.context.isRTL(),
      minPageSpread,
      isImageType,
      /**
       * When we have scrollable content, we use "native" touch event from the frame instead of
       * our own gestures.
       * @todo move this into scroll navigator
       */
      enableTouch: this.settings.values.computedPageTurnMode === `scrollable`,
    })

    return of(dims)
  }

  onRenderHeadless() {
    return from(this.resourcesHandler.fetchResource()).pipe(
      switchMap((resource) => {
        if (resource instanceof Response) {
          const contentType = resource.headers.get("content-type") ?? ""
          const parsableContentTypes: DOMParserSupportedType[] = [
            `application/xhtml+xml`,
            `application/xml`,
            `text/html`,
            `text/xml`,
          ]

          if (
            parsableContentTypes.includes(contentType as DOMParserSupportedType)
          ) {
            return from(resource.text()).pipe(
              map((text) => {
                const domParser = new DOMParser()
                const doc = domParser.parseFromString(
                  text,
                  contentType as DOMParserSupportedType,
                )

                return doc
              }),
            )
          }
        }

        return of(undefined)
      }),
    )
  }

  private isPrePaginated = () => {
    return (
      this.item.renditionLayout === `pre-paginated` ||
      (!this.item.renditionLayout &&
        this.context.manifest.renditionLayout === `pre-paginated`)
    )
  }

  private isImageType = () => {
    const mimeType =
      this.item.mediaType ?? detectMimeTypeFromName(this.item.href)

    return !!mimeType?.startsWith(`image/`)
  }

  private getFrameElement() {
    const frame = this.documentContainer

    if (!isHtmlTagElement(frame, "iframe")) return

    return frame
  }

  private prepareFrameForAttach(frameElement: HTMLIFrameElement) {
    /**
     * Keep the frame out of interaction/paint while it warms up.
     * `contain` limits parent invalidation during this preload phase.
     */
    frameElement.style.setProperty(`visibility`, `hidden`)
    frameElement.style.setProperty(`pointer-events`, `none`)
    frameElement.style.setProperty(`contain`, `layout paint size`)
    frameElement.style.setProperty(`display`, `block`)
  }

  private revealFrameAfterReady(frameElement: HTMLIFrameElement) {
    frameElement.style.removeProperty(`visibility`)
    frameElement.style.removeProperty(`pointer-events`)
    frameElement.style.removeProperty(`contain`)
  }

  // @todo optimize
  public getComputedStyleAfterLoad() {
    const frame = this.getFrameElement()

    const body = frame?.contentDocument?.body

    if (body) {
      return frame?.contentWindow?.getComputedStyle(body)
    }
  }

  get writingMode() {
    if (this.isImageType()) return undefined

    return this.getComputedStyleAfterLoad()?.writingMode as
      | `vertical-rl`
      | `horizontal-tb`
      | undefined
  }

  get readingDirection() {
    const writingMode = this.writingMode

    if (writingMode === `vertical-rl`) {
      return `rtl`
    }

    const direction = this.getComputedStyleAfterLoad()?.direction

    switch (direction) {
      case `ltr`:
      case `inherit`:
      case `initial`: {
        return `ltr`
      }

      case `rtl`:
        return `rtl`

      default:
        return undefined
    }
  }

  getDocumentFrame() {
    return this.getFrameElement()
  }
}
